// components/employee/EmployeeCard.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch } from "@/store/hooks";
import { deleteEmployee, updateEmployee } from "@/store/slices/employeeSlice";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { useState } from "react";
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native";

interface Employee {
  id: number;
  ownerName: string;
  email: string;
  phone: string;
  designation: string;
  employeeCode: string;
  isVerified: boolean; // true = active, false = inactive
}

interface EmployeeCardProps {
  employee: Employee;
  onRefresh: () => void;
}

export default function EmployeeCard({
  employee,
  onRefresh,
}: EmployeeCardProps) {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const isActive = employee.isVerified === true;

  const handleEdit = () => {
    router.push(`/(app)/employees/${employee.id}`);
  };

  const handleToggleStatus = async () => {
    const newStatus = isActive ? "deactivate" : "activate";
    Alert.alert(
      `${newStatus} Employee`,
      `Are you sure you want to ${newStatus} ${employee.ownerName}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: newStatus.toUpperCase(),
          onPress: async () => {
            setLoading(true);
            const result = await dispatch(
              updateEmployee({
                id: employee.id,
                data: { isActive: isActive ? 0 : 1 },
              }),
            );
            if (updateEmployee.fulfilled.match(result)) {
              onRefresh();
            }
            setLoading(false);
          },
        },
      ],
    );
  };

  const handleChangePassword = () => {
    router.push(`/(app)/employees/${employee.id}`);
  };

  const handleDelete = () => {
    Alert.alert(
      "Delete Employee",
      `Are you sure you want to delete ${employee.ownerName}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            setLoading(true);
            const result = await dispatch(deleteEmployee(employee.id));
            if (deleteEmployee.fulfilled.match(result)) {
              onRefresh();
            }
            setLoading(false);
          },
        },
      ],
    );
  };

  const styles = StyleSheet.create({
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: colors.text,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 1,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    name: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
    },
    code: {
      fontSize: 12,
      color: colors.textLight,
      marginTop: 2,
    },
    designation: {
      fontSize: 12,
      color: colors.primary,
      marginTop: 2,
    },
    email: {
      fontSize: 14,
      color: colors.textLight,
      marginTop: 4,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: 8,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      marginRight: 6,
    },
    statusText: {
      fontSize: 12,
      fontWeight: "500",
    },
    actions: {
      flexDirection: "row",
      marginTop: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    actionButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 6,
    },
    actionText: {
      fontSize: 12,
      marginLeft: 4,
      color: colors.textLight,
    },
    disabledButton: {
      opacity: 0.6,
    },
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View>
          <Text style={styles.name}>{employee.ownerName}</Text>
          <Text style={styles.code}>ID: {employee.employeeCode}</Text>
          <Text style={styles.designation}>{employee.designation}</Text>
        </View>
        <TouchableOpacity onPress={handleEdit} disabled={loading}>
          <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
        </TouchableOpacity>
      </View>

      <Text style={styles.email}>{employee.email}</Text>

      <View style={styles.statusBadge}>
        <View
          style={[
            styles.statusDot,
            { backgroundColor: isActive ? "#22c55e" : "#ef4444" },
          ]}
        />
        <Text
          style={[
            styles.statusText,
            { color: isActive ? "#22c55e" : "#ef4444" },
          ]}
        >
          {isActive ? "Active" : "Inactive"}
        </Text>
      </View>

      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleEdit}
          disabled={loading}
        >
          <Ionicons name="create-outline" size={18} color={colors.primary} />
          <Text style={styles.actionText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, loading && styles.disabledButton]}
          onPress={handleToggleStatus}
          disabled={loading}
        >
          <Ionicons
            name={isActive ? "eye-off-outline" : "eye-outline"}
            size={18}
            color="#f59e0b"
          />
          <Text style={styles.actionText}>
            {isActive ? "Deactivate" : "Activate"}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, loading && styles.disabledButton]}
          onPress={handleChangePassword}
          disabled={loading}
        >
          <Ionicons name="key-outline" size={18} color={colors.secondary} />
          <Text style={styles.actionText}>Change PW</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, loading && styles.disabledButton]}
          onPress={handleDelete}
          disabled={loading}
        >
          <Ionicons name="trash-outline" size={18} color={colors.danger} />
          <Text style={[styles.actionText, { color: colors.danger }]}>
            Delete
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
